import { Stack, ListGroup, Row, Col } from 'react-bootstrap';
import * as yup from 'yup';
import { AppData } from 'merchant-api/types';
import { FieldValues, Path } from 'react-hook-form';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { makeOptions} from '@odmonorepo/odfunctions';
import { FormSelect, FormInput, formatZipCode } from '@odmonorepo/core-components';
import { useAnalytics } from 'use-analytics';
import { fetchSmartySuggestions } from 'merchant-api/vendor';
import { useEffect } from 'react';

const AddressInputSchema = yup.object().shape({
  lineOne: yup.string().required(),
  city: yup.string().required(),
  state: yup.string().required(),
  postalCode: yup.string()
    .required()
    .test(
      'postalCode-length',
      'Postal code must be 5 or 9 digits',
      value => !!value && (/^\d{5}$/.test(value) || /^\d{9}$/.test(value))
    ),
});

export { AddressInputSchema };

export const AddressInput = <T extends FieldValues = AppData>({ namePrefix = "", compactStateZip = false }: { namePrefix?: string; compactStateZip?: boolean }) => {
  const { setValue, watch, register } = useFormContext();
  const analytics = useAnalytics();

  const field = (key: string) => (namePrefix ? `${namePrefix}.addresses.0.${key}` : `addresses.0.${key}`);

  const [addressQuery, setAddressQuery] = useState('');
  // Sync addressQuery with form value for lineOne
  const lineOneValue = watch(field('lineOne'));
  // Only update addressQuery if it is empty and form value exists (initial load or mock)
  useEffect(() => {
    if (typeof lineOneValue === 'string' && lineOneValue && addressQuery === '') {
      setAddressQuery(lineOneValue);
    }
  }, [lineOneValue]);
  const [suggestions, setSuggestions] = useState<any[]>([]);

  const fetchSuggestions = async (input: string) => {
    if (input.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const suggestions = await fetchSmartySuggestions(input);
      setSuggestions(suggestions);
      analytics.track('SmartyAPICall', { status: 'success' });
    } catch (err) {
      analytics.track('SmartyAPICall', { status: 'fail' });
      // Fail silently if Smarty Streets API fails
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (suggestion: any) => {
    setAddressQuery(suggestion.street_line);
    setSuggestions([]);
    setValue(field('lineOne'), suggestion.street_line);
    if (suggestion.city) setValue(field('city'), suggestion.city);
    if (suggestion.state) setValue(field('state'), suggestion.state);
    if (suggestion.zipcode) setValue(field('postalCode'), suggestion.zipcode);
  };


  return (
    <Stack gap={5}>
      <style>
        {`
          .list-group-item-action {
            color: #333 !important;
          }
        `}
      </style>
      <div style={{ position: 'relative' }}>
        <FormInput<T>
          {...{
            ...register(field('lineOne') as Path<T>, {
              onChange: (e) => {
                fetchSuggestions(e.target.value);
              }
            }),
            name: field('lineOne') as Path<T>
          }}
          data-testid="addressLineOne"
          required
        />
        {suggestions.length > 0 && (
          <ListGroup style={{borderRadius: '0.25rem', color: '#333', position: 'absolute', zIndex: '1000', width: '100%' }}>
            {suggestions.map((s, idx) => (
              <ListGroup.Item
                key={idx}
                action
                onClick={() => handleSuggestionSelect(s)}
              >
                <div className="lh-sm fw-medium">{s.street_line}</div>
                <div>{s.city}, {s.state} {s.zipcode}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>
      <FormInput<T> name={field('city') as Path<T>} data-testid="addressCity" />
      <Row xs={compactStateZip ? 2 : 1} md={2} className="g-5">
        <Col>
          <FormSelect<T>
            name={`${field('state')}` as Path<T>}
            data-testid="addressState"
            options={makeOptions('business.address.state', 51)}
            blankLabel=" "
          />
        </Col>
        <Col>
          <FormInput<T>
            name={`${field('postalCode')}` as Path<T>}
            data-testid="addressPostalCode"
            stripFormatOnSave
            formatValue={formatZipCode}
            controlProps={{ inputMode: 'numeric' }}
          />
        </Col>
      </Row>
    </Stack>
  );
};
